/**
 * Progress Page - Visual progress tracking with charts
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, Cell, PieChart, Pie, Legend
} from 'recharts';
import {
  BookOpen, Award, CheckCircle, TrendingUp, Flame,
  Calendar, Star, ArrowRight, Clock
} from 'lucide-react';

const COURSE_COLORS = {
  DOCKER:     '#3b82f6',
  KUBERNETES: '#6366f1',
  ANSIBLE:    '#f59e0b',
  RH124:      '#ef4444',
  RH134:      '#dc2626',
  SVRADM:     '#8b5cf6',
  TERRAFORM:  '#10b981',
  GIT:        '#f97316',
  PYTHON:     '#eab308',
  MYSQL:      '#06b6d4',
  JAVA:       '#e11d48',
  C_LANG:     '#64748b',
  CPP:        '#475569',
};

const getCourseColor = (course) => COURSE_COLORS[course] || '#94a3b8';

// Build last 30 days activity map from progress data
const buildActivityMap = (progressData) => {
  const map = {};
  progressData.forEach((item) => {
    if (!item.last_activity) return;
    const date = new Date(item.last_activity).toISOString().slice(0, 10);
    map[date] = (map[date] || 0) + 1;
  });
  return map;
};

// Last N days array
const lastNDays = (n) => {
  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
};

const ActivityHeatmap = ({ progressData }) => {
  const activityMap = buildActivityMap(progressData);
  const days = lastNDays(30);

  const weeks = [];
  let week = [];
  days.forEach((day, i) => {
    week.push(day);
    if (week.length === 7 || i === days.length - 1) {
      weeks.push([...week]);
      week = [];
    }
  });

  const getColor = (count) => {
    if (!count) return 'bg-slate-700';
    if (count === 1) return 'bg-emerald-900';
    if (count === 2) return 'bg-emerald-700';
    if (count >= 3) return 'bg-emerald-500';
    return 'bg-slate-700';
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div>
      <div className="flex gap-1 mb-1">
        {dayLabels.map(d => (
          <div key={d} className="w-7 text-[10px] text-slate-500 text-center">{d}</div>
        ))}
      </div>
      <div className="flex gap-1 flex-wrap">
        {days.map((day) => {
          const count = activityMap[day] || 0;
          const label = `${day}: ${count} lab${count !== 1 ? 's' : ''}`;
          return (
            <div
              key={day}
              title={label}
              className={`w-7 h-7 rounded-sm ${getColor(count)} transition-all hover:scale-110 cursor-default`}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-slate-500">Less</span>
        {['bg-slate-700', 'bg-emerald-900', 'bg-emerald-700', 'bg-emerald-500'].map((c, i) => (
          <div key={i} className={`w-4 h-4 rounded-sm ${c}`} />
        ))}
        <span className="text-xs text-slate-500">More</span>
      </div>
    </div>
  );
};

const CourseProgressBar = ({ course, completed, total, color }) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 text-sm font-medium text-slate-300 truncate">{course}</div>
      <div className="flex-1 bg-slate-700 rounded-full h-3 overflow-hidden">
        <div
          className="h-3 rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <div className="w-16 text-right text-sm text-slate-400">{completed}/{total}</div>
      <div className="w-10 text-right text-xs font-bold" style={{ color }}>{pct}%</div>
    </div>
  );
};

const Progress = () => {
  const [progressData, setProgressData] = useState([]);
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [showAllLabs, setShowAllLabs] = useState(false);

  useEffect(() => {
    Promise.all([
      apiService.getMyProgress(),
      apiService.getDashboardSummary().catch(() => null),
    ]).then(([prog, dash]) => {
      setProgressData(prog || []);
      setDashData(dash);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const courses = [...new Set(progressData.map((p) => p.course))].sort();

  const courseStats = courses.map((course) => {
    const items = progressData.filter((p) => p.course === course);
    const completed = items.filter((p) => p.completed_tasks === p.total_tasks && p.total_tasks > 0).length;
    const total = items.length;
    const score = items.reduce((s, p) => s + p.score, 0);
    const maxScore = items.reduce((s, p) => s + p.max_score, 0);
    return { course, completed, total, score, maxScore, color: getCourseColor(course) };
  });

  const filteredLabs = selectedCourse === 'all'
    ? progressData
    : progressData.filter((p) => p.course === selectedCourse);

  const handleCourseFilter = (course) => {
    setSelectedCourse(course);
    setShowAllLabs(false);
  };

  // Bar chart data: score per course
  const scoreChartData = courseStats.map((c) => ({
    name: c.course,
    score: c.score,
    max: c.maxScore,
    fill: c.color,
  }));

  // Pie chart: completed vs in-progress
  const totalCompleted = courseStats.reduce((s, c) => s + c.completed, 0);
  const totalInProgress = courseStats.reduce((s, c) => s + (c.total - c.completed), 0);
  const pieData = [
    { name: 'Completed', value: totalCompleted, fill: '#10b981' },
    { name: 'In Progress', value: totalInProgress, fill: '#6366f1' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
        <div className="max-w-7xl mx-auto animate-pulse space-y-6">
          <div className="h-8 bg-slate-700 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-slate-700 rounded-2xl" />)}
          </div>
          <div className="h-64 bg-slate-700 rounded-2xl" />
        </div>
      </div>
    );
  }

  const overallCompleted = courseStats.reduce((s, c) => s + c.completed, 0);
  const overallTotal = courseStats.reduce((s, c) => s + c.total, 0);
  const overallScore = courseStats.reduce((s, c) => s + c.score, 0);
  const overallMax = courseStats.reduce((s, c) => s + c.maxScore, 0);
  const streak = dashData?.streak_days ?? 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6" data-testid="progress-page">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">My Progress</h1>
          <p className="text-slate-400 mt-1">Track your learning journey across all courses</p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-5">
            <CheckCircle className="w-6 h-6 text-emerald-200 mb-2" />
            <div className="text-3xl font-bold text-white">{overallCompleted}</div>
            <div className="text-emerald-200 text-sm mt-1">Labs Completed</div>
            <div className="text-emerald-300 text-xs mt-1">of {overallTotal} attempted</div>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5">
            <Star className="w-6 h-6 text-blue-200 mb-2" />
            <div className="text-3xl font-bold text-white">{overallScore.toLocaleString()}</div>
            <div className="text-blue-200 text-sm mt-1">Total XP</div>
            <div className="text-blue-300 text-xs mt-1">of {overallMax.toLocaleString()} possible</div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-5">
            <Flame className="w-6 h-6 text-orange-200 mb-2" />
            <div className="text-3xl font-bold text-white">{streak}</div>
            <div className="text-orange-200 text-sm mt-1">Day Streak</div>
            <div className="text-orange-300 text-xs mt-1">{streak >= 7 ? '🔥 On fire!' : 'Keep going!'}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-5">
            <TrendingUp className="w-6 h-6 text-purple-200 mb-2" />
            <div className="text-3xl font-bold text-white">{courses.length}</div>
            <div className="text-purple-200 text-sm mt-1">Courses Active</div>
            <div className="text-purple-300 text-xs mt-1">
              {overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0}% completion rate
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Score per Course Bar Chart */}
          <div className="lg:col-span-2 bg-slate-800 rounded-2xl p-5 border border-slate-700">
            <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" /> Score by Course
            </h2>
            {scoreChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={scoreChartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                    labelStyle={{ color: '#f1f5f9' }}
                    formatter={(val, name) => [val, name === 'score' ? 'Score' : 'Max']}
                  />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                    {scoreChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
                Complete some labs to see your scores
              </div>
            )}
          </div>

          {/* Completed vs In-Progress Pie */}
          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
            <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-400" /> Lab Status
            </h2>
            {overallTotal > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                    labelStyle={{ color: '#f1f5f9' }}
                  />
                  <Legend
                    formatter={(value) => <span style={{ color: '#cbd5e1', fontSize: 12 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
                No labs started yet
              </div>
            )}
          </div>
        </div>

        {/* Activity Heatmap */}
        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" /> Activity — Last 30 Days
          </h2>
          <ActivityHeatmap progressData={progressData} />
        </div>

        {/* Course Progress Bars */}
        {courseStats.length > 0 && (
          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
            <h2 className="text-white font-semibold mb-5 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" /> Course Breakdown
            </h2>
            <div className="space-y-4">
              {courseStats.map((c) => (
                <CourseProgressBar
                  key={c.course}
                  course={c.course}
                  completed={c.completed}
                  total={c.total}
                  color={c.color}
                />
              ))}
            </div>
          </div>
        )}

        {/* Lab List */}
        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-slate-400" /> All Labs
            </h2>
            {/* Course filter */}
            <div className="flex gap-2 flex-wrap justify-end">
              <button
                onClick={() => handleCourseFilter('all')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  selectedCourse === 'all'
                    ? 'bg-white text-slate-900'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                }`}
              >
                All
              </button>
              {courses.map((c) => (
                <button
                  key={c}
                  onClick={() => handleCourseFilter(c)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    selectedCourse === c
                      ? 'text-white'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                  style={selectedCourse === c ? { backgroundColor: getCourseColor(c) } : {}}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filteredLabs.length > 0 ? (
            <div className="space-y-3" data-testid="progress-items">
              {(showAllLabs ? filteredLabs : filteredLabs.slice(0, 5)).map((item) => {
                const pct = item.total_tasks > 0
                  ? Math.round((item.completed_tasks / item.total_tasks) * 100)
                  : 0;
                const isComplete = item.completed_tasks === item.total_tasks && item.total_tasks > 0;
                const color = getCourseColor(item.course);
                return (
                  <div
                    key={item.long_id}
                    className="bg-slate-700/50 rounded-xl p-4 border border-slate-600 hover:border-slate-500 transition"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-white font-medium text-sm">{item.title}</span>
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: color }}
                          >
                            {item.course}
                          </span>
                          {isComplete && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                              ✓ Completed
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mb-2 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {item.completed_tasks}/{item.total_tasks} tasks
                          </span>
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {item.score}/{item.max_score} pts
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.last_activity
                              ? new Date(item.last_activity).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                              : '—'}
                          </span>
                        </div>
                        <div className="w-full bg-slate-600 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: isComplete ? '#10b981' : color }}
                          />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold text-white">{pct}%</div>
                        <Link
                          to={`/labs/${item.course}/${item.lab_id}`}
                          className="mt-1 flex items-center gap-1 text-xs text-slate-400 hover:text-white transition"
                        >
                          {isComplete ? 'Review' : 'Resume'} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredLabs.length > 5 && (
                <button
                  onClick={() => setShowAllLabs(!showAllLabs)}
                  className="w-full py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium transition"
                >
                  {showAllLabs ? '▲ Show Less' : `▼ Show All ${filteredLabs.length} Labs`}
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No labs found for this course</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Progress;
